export const links = {
  "github": "https://github.com/MarianoChaves",
  "email": "mailto:mchaves.software@gmail.com",
  "writing": "https://medium.com/@marianoechaves",
  "linkedin": "https://www.linkedin.com/in/mariano-echaves/",
  "scholar": "https://scholar.google.com/citations?user=6vMbe-YAAAAJ",
  "orcid": "https://orcid.org/0000-0001-7396-081X"
};
export const profile = {
  "en": {
    "skip": "Skip to content",
    "nav": [
      "Expertise",
      "Selected work",
      "Experience",
      "Contact"
    ],
    "role": "Data Scientist Manager · Itaú Unibanco",
    "degree": "PhD in Physics · UNICAMP",
    "headline": [
      "Rigorous science.",
      "Responsible AI."
    ],
    "intro": "Data Scientist Manager at Itaú and Physics PhD from UNICAMP. I work across independent validation, AI governance and software engineering, connecting mathematical depth to risk decisions and product development.",
    "workCta": "Explore my work",
    "contactCta": "Start a conversation",
    "note": "PHYSICS → INFERENCE → ENGINEERING → DECISION",
    "lens": "From assumptions to systems",
    "lensText": "A scientific approach to engineering: make assumptions explicit, test the evidence and understand the limits.",
    "lensItems": [
      [
        "01",
        "Model",
        "Statistical structure & uncertainty"
      ],
      [
        "02",
        "Evaluate",
        "Evidence, failure modes & risk"
      ],
      [
        "03",
        "Engineer",
        "Interfaces, runtimes & reproducibility"
      ]
    ],
    "aboutLabel": "PROFESSIONAL BIOGRAPHY",
    "aboutTitle": "From neutrino physics to evaluating and building AI.",
    "aboutText": "I am Mariano Esteves Chaves, Data Scientist Manager at Itaú Unibanco. I work in second-line independent model validation and generative AI governance, examining assumptions, data, implementation and failure modes to support decisions about model risk and use.",
    "expertiseTitle": "From hypothesis to systems in practice.",
    "expertiseLabel": "01 / EXPERTISE",
    "capabilities": [
      [
        "Independent validation & model risk",
        "Reviewing design, data, methodology and implementation. Connecting discrimination, calibration, stability and uncertainty to the materiality of model use and business decisions.",
        [
          "MRM",
          "Calibration",
          "Drift",
          "Fairness"
        ]
      ],
      [
        "Generative AI governance",
        "Risk criteria, traceability and evidence for evaluating LLMs and AI applications. Human oversight, usage boundaries and controls treated as architectural decisions.",
        [
          "GenAI",
          "LLMs",
          "Evaluation",
          "Governance"
        ]
      ],
      [
        "Adversarial AI evaluation",
        "Testing prompt injection, jailbreaks, information leakage, hallucinations and out-of-distribution inputs. Evaluations driven by failure modes and control effectiveness.",
        [
          "Red teaming",
          "Robustness",
          "Guardrails",
          "Security"
        ]
      ],
      [
        "Agent & software engineering",
        "Explicit interfaces between models, tools and execution. Python tooling, deterministic tests, execution limits and inspectable outcomes.",
        [
          "Python",
          "Agents",
          "APIs",
          "Testing"
        ]
      ],
      [
        "Inference & scientific computing",
        "Neutrino modeling, Monte Carlo methods, Bayesian inference and optimization. C++ and Python implementations grounded in physical assumptions and numerical behavior.",
        [
          "PhD",
          "C++",
          "MCMC",
          "Numerical methods"
        ]
      ],
      [
        "Cloud & technical direction",
        "AWS pipelines, services and infrastructure. Bridging risk, engineering and business through architectural decisions, validation automation and maintainable software.",
        [
          "AWS",
          "SQL",
          "IaC",
          "Technical leadership"
        ]
      ]
    ],
    "projectsLabel": "02 / SELECTED WORK",
    "projectsTitle": "From research to product.",
    "projectsIntro": "Adesivia and Pygent bring AI engineering into focus. Libraries for inference, simulation and optimization show the mathematical foundations behind that work.",
    "source": "Explore repository",
    "details": "Technical notes",
    "evidence": "Implementation",
    "projects": [
      {
        "name": "Adesivia",
        "id": "adesivia",
        "featured": true,
        "url": "https://adesivia.com",
        "linkLabel": "Visit Adesivia",
        "category": "GENERATIVE AI PRODUCT",
        "tags": [
          "Angular",
          "Python",
          "AWS",
          "Terraform"
        ],
        "summary": "A generative AI project connecting image creation and editing to personalized products, with galleries, credits and a purchasing flow.",
        "problem": "Turn an idea into an image and a customizable product while coordinating generation latency, generated assets and user credits.",
        "design": "Angular/TypeScript interface and Python backend on AWS. API Gateway and Lambda accept requests; SQS decouples generation, DynamoDB records job state and credits, and S3 stores images. Image generation and editing APIs, Terraform infrastructure and GitHub Actions deployment.",
        "boundary": "An independently developed project with private source code. My work spans the user experience, AI integration and service architecture; image generation uses external models."
      },
      {
        "name": "Pygent",
        "category": "AI ENGINEERING",
        "repo": "pygent",
        "featured": true,
        "tags": [
          "Python",
          "Agents",
          "JSON Schema",
          "Evaluation"
        ],
        "summary": "Python tooling for AI agents with input validation, approvals, execution limits and inspectable results.",
        "problem": "An agent needs more than a model call: it needs a workspace, tool contracts and recoverable session state.",
        "design": "Connects model and tool interfaces with JSON Schema, execution budgets, events and deterministic testing. File and command runtimes are enabled explicitly.",
        "boundary": "Version 2.0.0rc1 is a release candidate. Synchronous in-process execution and cooperative deadlines; I/O timeouts belong to integrations.",
        "file": "docs/harness.md"
      },
      {
        "name": "nuMCMC",
        "category": "STATISTICAL COMPUTING",
        "repo": "MCMC",
        "tags": [
          "C++",
          "Bayesian inference",
          "Ensemble sampling"
        ],
        "summary": "A C++ library for Markov Chain Monte Carlo sampling, with walker and ensemble abstractions.",
        "problem": "Explore parameter distributions when uncertainty matters as much as a point estimate.",
        "design": "Organizes sampling into walkers, ensembles and an MCMC interface, with numerical examples and Python analysis scripts.",
        "boundary": "Scientific library archived on Zenodo as version 0.2, DOI 10.5281/zenodo.5496330. Examples support evaluating sampling behavior for each inference problem.",
        "file": "src/numcmc/src/ensemble.cpp"
      },
      {
        "name": "Neutrino simulator",
        "category": "COMPUTATIONAL PHYSICS",
        "repo": "neutrino-simulator",
        "tags": [
          "C++",
          "GSL",
          "Numerical integration"
        ],
        "summary": "A personal simulation engine for neutrino experiments, connecting physics inputs to numerical computation.",
        "problem": "Represent physical inputs on discrete grids and transform them into quantities useful for experimental simulation.",
        "design": "Includes binned data structures, two-dimensional interpolation with GSL, numerical integration and reactor input data.",
        "boundary": "Research prototype; the repository describes its KamLAND example as under construction.",
        "file": "src/engine/Binned2D.cpp"
      },
      {
        "name": "nuDEV",
        "category": "NUMERICAL OPTIMIZATION",
        "repo": "nuDEV",
        "tags": [
          "C++",
          "Differential evolution"
        ],
        "summary": "An exploratory implementation of differential evolution in C++.",
        "problem": "Express population-based optimization through reusable numerical components.",
        "design": "Structures the algorithm around population, mutant and evolution classes, with a test example.",
        "boundary": "Early research implementation (v0.0.1), included to show algorithmic foundations.",
        "file": "nuDEV/src/dev.cpp"
      }
    ],
    "problem": "Problem",
    "design": "Design",
    "boundary": "Scope",
    "experienceLabel": "03 / EXPERIENCE",
    "experienceTitle": "A path through science, software and risk.",
    "experience": [
      [
        "NOW",
        "Data Scientist Manager",
        "Itaú Unibanco",
        "Independent model validation, Model Risk Management and generative AI governance. Technical leadership across development, business, risk, compliance and audit.",
        [
          "Review of model design, data, methodology, implementation, calibration, stability, drift, explainability and fairness.",
          "Adversarial LLM testing: prompt injection, jailbreaks, information leakage, hallucinations and out-of-distribution inputs.",
          "Technical leadership in validation automation, increasing the capacity for validated models by more than threefold.",
          "Risk criteria, evidence, traceability, human oversight and prioritization by failure probability and financial impact."
        ]
      ],
      [
        "2024",
        "Software & Machine Learning Engineering",
        "Wingbits · Sweden / remote",
        "ADS-B flight-data systems, pipelines, APIs and geospatial components. Distributed services and AWS infrastructure using Lambda, DynamoDB, S3, Glue, Athena and ECS Fargate.",
        [
          "Development of APIs, monitoring and infrastructure as code for ADS-B data.",
          "Public contributions to the Wingbits configuration repository, including data-collection software releases."
        ]
      ],
      [
        "2022",
        "PhD in Physics",
        "UNICAMP",
        "Thesis: “Search for contact interactions in neutrinos: source and detection”, supervised by Orlando Luis Goulart Peres. Oscillation modeling, non-standard interactions, numerical simulation and analysis of experimental data."
      ],
      [
        "2016 → 2018",
        "BSc & MSc in Physics",
        "Universidade Federal Fluminense",
        "Training in physics, mathematical methods and scientific research. The foundation for connecting theory, computation and quantitative analysis."
      ]
    ],
    "approachLabel": "HOW I WORK",
    "approachTitle": "Make the reasoning as clear as the implementation.",
    "principles": [
      [
        "Frame what is at stake",
        "Start with the decision, the use context and the cost of failure. Give each assumption a test and each metric an interpretation."
      ],
      [
        "Build evidence, not just results",
        "Combine statistical evaluation, adversarial testing and implementation review. Explain what a model supports, where it fails and which controls matter."
      ],
      [
        "Turn depth into direction",
        "Document choices and boundaries, design clear interfaces and align people across disciplines. Make the work reproducible and useful beyond its original author."
      ]
    ],
    "writingTitle": "Ideas, explained.",
    "writingText": "Technical writing on AI and computing.",
    "writingCta": "Read on Medium",
    "contactLabel": "04 / LET’S TALK",
    "contactTitle": "Complex problems deserve clear thinking.",
    "contactText": "Let’s talk about AI evaluation, data science leadership, model risk and systems that need to earn trust.",
    "emailCta": "Get in touch",
    "footer": "Mariano Esteves Chaves · Science, software & systems.",
    "biography": [
      "I earned my BSc in Physics in 2016 and MSc in 2018 at UFF, followed by a PhD at UNICAMP in 2022. My research covered neutrino oscillations and non-standard interactions, with papers in JHEP and Journal of Physics G and collaboration with Thomas Schwetz at Karlsruhe Institute of Technology.",
      "At Wingbits in 2024, I worked remotely with a Swedish team on aviation-data systems and AWS infrastructure. That engineering experience also informs Adesivia, my generative AI project for images and personalized products, Pygent and my scientific C++ libraries."
    ],
    "researchLabel": "RESEARCH AND PUBLICATIONS",
    "researchTitle": "Hypotheses tested against data.",
    "researchIntro": "Selected work in neutrino physics. Each paper connects a mathematical model to quantitative analysis and makes the scope of its conclusions explicit.",
    "publications": [
      {
        "year": "2023",
        "title": "Testing non-standard neutrino interactions in (anti)-electron neutrino disappearance experiments",
        "authors": "M. E. Chaves, P. C. de Holanda, O. L. G. Peres",
        "venue": "Journal of High Energy Physics · 03 (2023) 180",
        "url": "https://doi.org/10.1007/JHEP03(2023)180",
        "description": "Analysis of scalar and tensor interactions using electron-neutrino and antineutrino disappearance data."
      },
      {
        "year": "2021",
        "title": "Resolving the LMA-dark NSI degeneracy with coherent neutrino-nucleus scattering",
        "authors": "Mariano Esteves Chaves, Thomas Schwetz",
        "venue": "Journal of High Energy Physics · 05 (2021) 042",
        "url": "https://doi.org/10.1007/JHEP05(2021)042",
        "description": "Combines oscillation and coherent-scattering data to study parameter degeneracies and the sensitivity of future experiments."
      },
      {
        "year": "2021",
        "title": "Improvements on perturbative oscillation formulas including non-standard neutrino interactions",
        "authors": "M. E. Chaves, D. R. Gratieri, O. L. G. Peres",
        "venue": "Journal of Physics G · 48 (2021) · online 2020",
        "url": "https://doi.org/10.1088/1361-6471/abae17",
        "description": "Perturbative approximations for oscillation probabilities, with analysis of parameter degeneracies."
      }
    ],
    "readPaper": "Read paper",
    "thesisLink": "UNICAMP thesis",
    "languages": "Native Portuguese · Advanced English"
  },
  "pt": {
    "skip": "Pular para o conteúdo",
    "nav": [
      "Competências",
      "Projetos",
      "Experiência",
      "Contato"
    ],
    "role": "Data Scientist Manager · Itaú Unibanco",
    "degree": "Doutor em Física · UNICAMP",
    "headline": [
      "Ciência rigorosa.",
      "IA com responsabilidade."
    ],
    "intro": "Data Scientist Manager no Itaú e doutor em Física pela Unicamp. Trabalho com validação independente, governança de IA e engenharia de software, conectando profundidade matemática a decisões de risco e desenvolvimento de produtos.",
    "workCta": "Explore meus projetos",
    "contactCta": "Vamos conversar",
    "note": "FÍSICA → INFERÊNCIA → ENGENHARIA → DECISÃO",
    "lens": "Das hipóteses aos sistemas",
    "lensText": "Uma abordagem científica à engenharia: explicitar hipóteses, testar evidências e compreender os limites.",
    "lensItems": [
      [
        "01",
        "Modelar",
        "Estrutura estatística e incerteza"
      ],
      [
        "02",
        "Avaliar",
        "Evidências, modos de falha e risco"
      ],
      [
        "03",
        "Construir",
        "Interfaces, execução e reprodutibilidade"
      ]
    ],
    "aboutLabel": "BIOGRAFIA PROFISSIONAL",
    "aboutTitle": "Da física de neutrinos à avaliação e construção de IA.",
    "aboutText": "Sou Mariano Esteves Chaves, Data Scientist Manager no Itaú Unibanco. Atuo em segunda linha na validação independente de modelos e na governança de IA generativa: examino hipóteses, dados, implementação e modos de falha para sustentar decisões sobre risco e uso de modelos.",
    "expertiseTitle": "Da hipótese ao sistema em operação.",
    "expertiseLabel": "01 / COMPETÊNCIAS",
    "capabilities": [
      [
        "Validação independente e risco de modelos",
        "Avaliação de desenho, dados, metodologia e implementação. Discriminação, calibração, estabilidade e incerteza conectadas à materialidade do uso e às decisões de negócio.",
        [
          "MRM",
          "Calibração",
          "Drift",
          "Fairness"
        ]
      ],
      [
        "Governança de IA generativa",
        "Critérios de risco, rastreabilidade e evidências para avaliar LLMs e aplicações de IA. Supervisão humana, limites de uso e controles tratados como parte da arquitetura.",
        [
          "GenAI",
          "LLMs",
          "Avaliação",
          "Governança"
        ]
      ],
      [
        "Testes adversariais de IA",
        "Avaliação de prompt injection, jailbreaks, vazamento de informação, alucinações e entradas fora de distribuição. Testes orientados a modos de falha e à efetividade dos controles.",
        [
          "Red teaming",
          "Robustez",
          "Guardrails",
          "Segurança"
        ]
      ],
      [
        "Engenharia de agentes e software",
        "Interfaces explícitas entre modelos, ferramentas e execução. Desenvolvimento de ferramentas Python, testes determinísticos, limites de execução e resultados inspecionáveis.",
        [
          "Python",
          "Agentes",
          "APIs",
          "Testes"
        ]
      ],
      [
        "Inferência e computação científica",
        "Modelagem de neutrinos, Monte Carlo, inferência bayesiana e otimização. Implementações em C++ e Python com atenção às hipóteses físicas e ao comportamento numérico.",
        [
          "PhD",
          "C++",
          "MCMC",
          "Métodos numéricos"
        ]
      ],
      [
        "Cloud e direção técnica",
        "Pipelines, serviços e infraestrutura AWS. Tradução entre risco, desenvolvimento e negócio; decisões de arquitetura, automação de validações e software que outras pessoas conseguem manter.",
        [
          "AWS",
          "SQL",
          "IaC",
          "Liderança técnica"
        ]
      ]
    ],
    "projectsLabel": "02 / PROJETOS SELECIONADOS",
    "projectsTitle": "Da pesquisa ao produto.",
    "projectsIntro": "Adesivia e Pygent colocam a engenharia de IA em primeiro plano. Bibliotecas de inferência, simulação e otimização mostram os fundamentos matemáticos por trás desse trabalho.",
    "source": "Explorar repositório",
    "details": "Notas técnicas",
    "evidence": "Implementação",
    "projects": [
      {
        "name": "Adesivia",
        "id": "adesivia",
        "featured": true,
        "url": "https://adesivia.com",
        "linkLabel": "Conhecer o Adesivia",
        "category": "PRODUTO DE IA GENERATIVA",
        "tags": [
          "Angular",
          "Python",
          "AWS",
          "Terraform"
        ],
        "summary": "Projeto de IA generativa que conecta criação e edição de imagens à personalização de produtos, com galerias, créditos e uma jornada de compra.",
        "problem": "Transformar uma ideia em imagem e produto personalizável, coordenando a espera pela geração, os arquivos produzidos e o saldo de créditos do usuário.",
        "design": "Interface Angular/TypeScript e backend Python em AWS. API Gateway e Lambda recebem pedidos; SQS desacopla a geração, DynamoDB registra estado e créditos, e S3 armazena imagens. Integração com APIs de geração e edição de imagens, infraestrutura Terraform e publicação por GitHub Actions.",
        "boundary": "Projeto próprio com código privado. Meu trabalho reúne a experiência de uso, integração de IA e arquitetura dos serviços; a geração de imagens utiliza modelos externos."
      },
      {
        "name": "Pygent",
        "category": "ENGENHARIA DE IA",
        "repo": "pygent",
        "featured": true,
        "tags": [
          "Python",
          "Agentes",
          "JSON Schema",
          "Avaliação"
        ],
        "summary": "Ferramentas Python para agentes de IA com validação de entradas, aprovações, limites de execução e resultados inspecionáveis.",
        "problem": "Um agente precisa de mais que uma chamada ao modelo: ambiente de trabalho, contratos de ferramentas e estado de sessão recuperável.",
        "design": "Combina interfaces de modelos e ferramentas com JSON Schema, orçamentos de execução, eventos e testes determinísticos. O runtime de arquivos e comandos é habilitado explicitamente.",
        "boundary": "A versão 2.0.0rc1 é candidata a lançamento. Execução síncrona em processo; deadlines cooperativos, com timeouts de I/O configurados nas integrações.",
        "file": "docs/harness.md"
      },
      {
        "name": "nuMCMC",
        "category": "COMPUTAÇÃO ESTATÍSTICA",
        "repo": "MCMC",
        "tags": [
          "C++",
          "Inferência bayesiana",
          "Ensemble sampling"
        ],
        "summary": "Biblioteca C++ para amostragem Markov Chain Monte Carlo, com abstrações de walkers e ensembles.",
        "problem": "Explorar distribuições de parâmetros quando a incerteza é tão relevante quanto uma estimativa pontual.",
        "design": "Organiza a amostragem em walkers, ensembles e uma interface MCMC, com exemplos numéricos e scripts de análise em Python.",
        "boundary": "Biblioteca científica publicada no Zenodo, versão 0.2, com DOI 10.5281/zenodo.5496330. Exemplos permitem avaliar o comportamento da amostragem em cada problema.",
        "file": "src/numcmc/src/ensemble.cpp"
      },
      {
        "name": "Neutrino simulator",
        "category": "FÍSICA COMPUTACIONAL",
        "repo": "neutrino-simulator",
        "tags": [
          "C++",
          "GSL",
          "Integração numérica"
        ],
        "summary": "Motor pessoal de simulação de experimentos de neutrinos, conectando insumos físicos à computação numérica.",
        "problem": "Representar entradas físicas em malhas discretas e transformá-las em grandezas úteis à simulação experimental.",
        "design": "Inclui estruturas de dados em bins, interpolação bidimensional com GSL, integração numérica e dados de entrada de reatores.",
        "boundary": "Protótipo de pesquisa; o repositório descreve o exemplo KamLAND como em construção.",
        "file": "src/engine/Binned2D.cpp"
      },
      {
        "name": "nuDEV",
        "category": "OTIMIZAÇÃO NUMÉRICA",
        "repo": "nuDEV",
        "tags": [
          "C++",
          "Evolução diferencial"
        ],
        "summary": "Implementação exploratória de evolução diferencial em C++.",
        "problem": "Expressar otimização baseada em populações com componentes numéricos reutilizáveis.",
        "design": "Estrutura o algoritmo em classes de população, mutação e evolução, com um exemplo de teste.",
        "boundary": "Implementação inicial de pesquisa (v0.0.1), incluída para demonstrar fundamentos algorítmicos.",
        "file": "nuDEV/src/dev.cpp"
      }
    ],
    "problem": "Problema",
    "design": "Arquitetura",
    "boundary": "Escopo",
    "experienceLabel": "03 / EXPERIÊNCIA",
    "experienceTitle": "Uma trajetória entre ciência, software e risco.",
    "experience": [
      [
        "ATUAL",
        "Data Scientist Manager",
        "Itaú Unibanco",
        "Validação independente de modelos, Model Risk Management e governança de IA generativa. Liderança técnica e articulação com desenvolvimento, negócio, risco, compliance e auditoria.",
        [
          "Avaliação de desenho, dados, metodologia, implementação, calibração, estabilidade, drift, explicabilidade e fairness.",
          "Testes adversariais de LLMs: prompt injection, jailbreaks, vazamento de informação, alucinações e entradas fora de distribuição.",
          "Liderança técnica na automação de validações, ampliando em mais de três vezes a capacidade de modelos validados.",
          "Critérios de risco, evidências, rastreabilidade, supervisão humana e priorização por probabilidade de falha e impacto financeiro."
        ]
      ],
      [
        "2024",
        "Engenharia de Software e Machine Learning",
        "Wingbits · Suécia / remoto",
        "Sistemas de dados de voos ADS-B, pipelines, APIs e componentes geoespaciais. Engenharia de serviços distribuídos e infraestrutura AWS, com Lambda, DynamoDB, S3, Glue, Athena e ECS Fargate.",
        [
          "Desenvolvimento de APIs, monitoramento e infraestrutura como código para dados ADS-B.",
          "Contribuições públicas no repositório de configuração da Wingbits, incluindo releases do software de coleta."
        ]
      ],
      [
        "2022",
        "Doutorado em Física",
        "UNICAMP",
        "Tese: “Busca por interações de contato em neutrinos: fonte e detecção”, sob orientação de Orlando Luis Goulart Peres. Modelagem de oscilações, interações não padrão, simulação numérica e análise de dados experimentais."
      ],
      [
        "2016 → 2018",
        "Graduação e mestrado em Física",
        "Universidade Federal Fluminense",
        "Formação em física, métodos matemáticos e investigação científica. Base para a conexão entre teoria, computação e análise quantitativa."
      ]
    ],
    "approachLabel": "COMO TRABALHO",
    "approachTitle": "Tornar o raciocínio tão claro quanto a implementação.",
    "principles": [
      [
        "Definir o que está em jogo",
        "Começar pela decisão, pelo contexto de uso e pelo custo de uma falha. Dar a cada hipótese um teste e a cada métrica uma interpretação."
      ],
      [
        "Construir evidência, não só resultados",
        "Combinar avaliação estatística, testes adversariais e revisão de implementação. Explicitar o que o modelo sustenta, onde falha e quais controles fazem diferença."
      ],
      [
        "Transformar profundidade em direção",
        "Documentar escolhas e limites, criar interfaces claras e alinhar pessoas de diferentes áreas. Tornar o trabalho reproduzível e útil além de quem o construiu."
      ]
    ],
    "writingTitle": "Ideias, explicadas.",
    "writingText": "Textos técnicos sobre IA e computação.",
    "writingCta": "Ler no Medium",
    "contactLabel": "04 / VAMOS CONVERSAR",
    "contactTitle": "Problemas complexos merecem clareza.",
    "contactText": "Vamos conversar sobre avaliação de IA, liderança em ciência de dados, risco de modelos e sistemas que precisam merecer confiança.",
    "emailCta": "Entrar em contato",
    "footer": "Mariano Esteves Chaves · Ciência, software e sistemas.",
    "biography": [
      "Minha formação começou na UFF, com graduação em Física em 2016 e mestrado em 2018, e seguiu na Unicamp, onde concluí o doutorado em 2022. Pesquisei oscilações de neutrinos e interações não padrão, com publicações em JHEP e Journal of Physics G e colaboração com Thomas Schwetz, do Karlsruhe Institute of Technology.",
      "Na Wingbits, em 2024, trabalhei remotamente com uma equipe na Suécia em sistemas de dados de aviação e infraestrutura AWS. Essa experiência de engenharia também aparece no Adesivia, projeto de IA generativa para imagens e produtos personalizados, no Pygent e nas minhas bibliotecas científicas em C++."
    ],
    "researchLabel": "PESQUISA E PUBLICAÇÕES",
    "researchTitle": "Hipóteses confrontadas com dados.",
    "researchIntro": "Trabalhos selecionados em física de neutrinos. Cada publicação conecta um modelo matemático a uma análise quantitativa e explicita o alcance das conclusões.",
    "publications": [
      {
        "year": "2023",
        "title": "Testing non-standard neutrino interactions in (anti)-electron neutrino disappearance experiments",
        "authors": "M. E. Chaves, P. C. de Holanda, O. L. G. Peres",
        "venue": "Journal of High Energy Physics · 03 (2023) 180",
        "url": "https://doi.org/10.1007/JHEP03(2023)180",
        "description": "Análise de interações escalares e tensoriais com dados de desaparecimento de neutrinos e antineutrinos de elétron."
      },
      {
        "year": "2021",
        "title": "Resolving the LMA-dark NSI degeneracy with coherent neutrino-nucleus scattering",
        "authors": "Mariano Esteves Chaves, Thomas Schwetz",
        "venue": "Journal of High Energy Physics · 05 (2021) 042",
        "url": "https://doi.org/10.1007/JHEP05(2021)042",
        "description": "Combinação de dados de oscilações e espalhamento coerente para estudar degenerescências e a sensibilidade de futuros experimentos."
      },
      {
        "year": "2021",
        "title": "Improvements on perturbative oscillation formulas including non-standard neutrino interactions",
        "authors": "M. E. Chaves, D. R. Gratieri, O. L. G. Peres",
        "venue": "Journal of Physics G · 48 (2021) · online 2020",
        "url": "https://doi.org/10.1088/1361-6471/abae17",
        "description": "Aproximações perturbativas para probabilidades de oscilação, com análise de degenerescências entre parâmetros."
      }
    ],
    "readPaper": "Ler publicação",
    "thesisLink": "Tese na Unicamp",
    "languages": "Português nativo · Inglês avançado"
  }
};
